import { useState } from "react";
import { ArrowUpDown, ArrowUp, ArrowDown, Info } from "lucide-react";
import type { RankingRow } from "./data/types";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

type Props = {
  title: string;
  rows: RankingRow[];
  showVoice?: boolean;
  showLanguage?: boolean;
};

// Hardcoded per provider-model benchmarks
// Source: ttfc_results.json + public pricing pages
const PROVIDER_STATS: Record<string, { ttfcMs: number; costPer1kChars: number | null }> = {
  "cartesia/sonic-3-2026-01-12": { ttfcMs: 359, costPer1kChars: 0.065 },
  "cartesia/sonic-3.5-2026-05-04": { ttfcMs: 350, costPer1kChars: 0.065 },
  "cartesia/sonic-3": { ttfcMs: 359, costPer1kChars: 0.065 },
  "elevenlabs/eleven_multilingual_v2": { ttfcMs: 1089, costPer1kChars: 0.30 },
  "elevenlabs/eleven_turbo_v2_5": { ttfcMs: 800, costPer1kChars: 0.18 },
  "inworld/tts-exploration-balanced": { ttfcMs: 565, costPer1kChars: null },
  "gemini/gemini-3.1-flash-tts-preview": { ttfcMs: null, costPer1kChars: null },
  "sarvam/bulbul:v3": { ttfcMs: 1621, costPer1kChars: 0.05 },
  "stepaudio/stepaudio-2.5-tts": { ttfcMs: null, costPer1kChars: null },
  "openai/tts-1": { ttfcMs: 400, costPer1kChars: 0.015 },
  "openai/tts-1-hd": { ttfcMs: 600, costPer1kChars: 0.030 },
};

function getStats(provider: string, model: string) {
  const key = `${provider}/${model}`.toLowerCase();
  return PROVIDER_STATS[key] ?? { ttfcMs: null, costPer1kChars: null };
}

const PROVIDER_COLORS: Record<string, string> = {
  cartesia: "#6366f1",
  inworld: "#0ea5e9",
  gemini: "#4285f4",
  sarvam: "#f59e0b",
  stepaudio: "#10b981",
  openai: "#10a37f",
  elevenlabs: "#f97316",
};

function providerColor(provider: string) {
  return PROVIDER_COLORS[provider.toLowerCase()] ?? "#94a3b8";
}

function RankBadge({ rank }: { rank: number }) {
  if (rank <= 3) {
    const emoji = rank === 1 ? "🥇" : rank === 2 ? "🥈" : "🥉";
    return <span className="text-lg leading-none">{emoji}</span>;
  }
  return (
    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
      {rank}
    </span>
  );
}

function EloBar({ rating, maxRating }: { rating: number; maxRating: number }) {
  const pct = maxRating > 0 ? Math.min(100, (rating / maxRating) * 100) : 0;
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className="text-sm font-semibold tabular-nums w-12 shrink-0">{rating}</span>
      <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden min-w-[40px]">
        <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

type SortKey = "rank" | "elo" | "wins" | "losses" | "comparisons" | "ttfc" | "cost";
type SortDir = "asc" | "desc";

type EnrichedRow = RankingRow & { ttfcMs: number | null; costPer1kChars: number | null };

export function ArenaLeaderboard({ title, rows, showVoice = false, showLanguage = false }: Props) {
  const [sortKey, setSortKey] = useState<SortKey>("rank");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const maxRating = rows.length > 0 ? Math.max(...rows.map((r) => r.rating)) : 1500;

  const enriched: EnrichedRow[] = rows.map((row) => {
    const stats = getStats(row.provider, row.model);
    return { ...row, ...stats };
  });

  const sorted = [...enriched].sort((a, b) => {
    let val = 0;
    switch (sortKey) {
      case "rank": val = a.rank - b.rank; break;
      case "elo": val = b.rating - a.rating; break;
      case "wins": val = b.wins - a.wins; break;
      case "losses": val = b.losses - a.losses; break;
      case "comparisons": val = b.comparisons - a.comparisons; break;
      case "ttfc": {
        if (a.ttfcMs === null && b.ttfcMs === null) val = 0;
        else if (a.ttfcMs === null) val = 1;
        else if (b.ttfcMs === null) val = -1;
        else val = a.ttfcMs - b.ttfcMs;
        break;
      }
      case "cost": {
        if (a.costPer1kChars === null && b.costPer1kChars === null) val = 0;
        else if (a.costPer1kChars === null) val = 1;
        else if (b.costPer1kChars === null) val = -1;
        else val = a.costPer1kChars - b.costPer1kChars;
        break;
      }
    }
    return sortDir === "asc" ? val : -val;
  });

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "rank" ? "asc" : "desc");
    }
  }

  function SortIcon({ col }: { col: SortKey }) {
    if (sortKey !== col) return <ArrowUpDown size={11} className="text-muted-foreground/50 ml-1 shrink-0" />;
    return sortDir === "asc"
      ? <ArrowUp size={11} className="text-foreground ml-1 shrink-0" />
      : <ArrowDown size={11} className="text-foreground ml-1 shrink-0" />;
  }

  const colCount = 9 + (showVoice ? 1 : 0) + (showLanguage ? 1 : 0);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold">{title}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30">
                <Th col="rank" active={sortKey} dir={sortDir} onSort={handleSort} label="Rank" />
                <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Provider</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Model</th>
                {showVoice && <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Voice</th>}
                {showLanguage && <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Language</th>}
                <Th col="elo" active={sortKey} dir={sortDir} onSort={handleSort} label="ELO" info="Bradley-Terry ELO rating" minW="min-w-32" />
                <Th col="wins" active={sortKey} dir={sortDir} onSort={handleSort} label="W" />
                <Th col="losses" active={sortKey} dir={sortDir} onSort={handleSort} label="L" />
                <Th col="comparisons" active={sortKey} dir={sortDir} onSort={handleSort} label="Comps" info="Total pairwise comparisons" />
                <Th col="ttfc" active={sortKey} dir={sortDir} onSort={handleSort} label="TTFC" info="Time to first audio chunk (ms) — from ttfc_results.json" />
                <Th col="cost" active={sortKey} dir={sortDir} onSort={handleSort} label="$/1k" info="Cost per 1,000 characters (USD)" />
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={colCount} className="text-center text-muted-foreground py-10 text-sm">
                    No votes recorded yet — start comparing in the Arena.
                  </td>
                </tr>
              ) : sorted.map((row) => (
                <tr key={row.entityId} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-3"><RankBadge rank={row.rank} /></td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full shrink-0" style={{ background: providerColor(row.provider) }} />
                      <span className="font-medium capitalize">{row.provider}</span>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">{row.model}</td>
                  {showVoice && (
                    <td className="px-4 py-3 text-xs">{row.voiceName ?? <span className="text-muted-foreground">—</span>}</td>
                  )}
                  {showLanguage && (
                    <td className="px-4 py-3">
                      {row.language
                        ? <Badge variant="secondary" className="text-xs capitalize">{row.language}</Badge>
                        : <span className="text-muted-foreground">—</span>}
                    </td>
                  )}
                  <td className="px-4 py-3 min-w-32"><EloBar rating={row.rating} maxRating={maxRating} /></td>
                  <td className="px-4 py-3 font-semibold text-green-700">{row.wins}</td>
                  <td className="px-4 py-3 font-semibold text-red-600">{row.losses}</td>
                  <td className="px-4 py-3 text-muted-foreground">{row.comparisons}</td>
                  <td className="px-4 py-3 tabular-nums text-xs">
                    {row.ttfcMs !== null
                      ? <span className="font-medium">{row.ttfcMs}ms</span>
                      : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-xs">
                    {row.costPer1kChars !== null
                      ? <span className="font-medium">${row.costPer1kChars.toFixed(3)}</span>
                      : <span className="text-muted-foreground">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function Th({
  col, active, dir, onSort, label, info, minW,
}: {
  col: SortKey;
  active: SortKey;
  dir: SortDir;
  onSort: (k: SortKey) => void;
  label: string;
  info?: string;
  minW?: string;
}) {
  const isActive = active === col;
  return (
    <th
      className={`text-left px-4 py-2.5 text-xs font-medium text-muted-foreground cursor-pointer select-none hover:text-foreground transition-colors ${minW ?? ""}`}
      onClick={() => onSort(col)}
    >
      <span className="inline-flex items-center gap-0.5">
        {label}
        {info && <Info size={10} className="opacity-50" title={info} />}
        {isActive
          ? (dir === "asc" ? <ArrowUp size={11} className="text-foreground" /> : <ArrowDown size={11} className="text-foreground" />)
          : <ArrowUpDown size={11} className="opacity-30" />
        }
      </span>
    </th>
  );
}
